import React, { useEffect, useState } from 'react';
import './AttractionsList.css';
import AttractionCard from '../attractionCard/AttractionCard';
import SearchHeader from './searchHeader/SearchHeader';
import MapView from '../mapview/MapView';
import Calculator from '../calculator/Calculator';
import attractionStore from '../../store/AttractionStore';
import { HeartOutlined, HeartFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import languageStore from '../../store/languageStore';
import { useNavigate } from 'react-router-dom';
import SearchAndFilter from '../searchFilter/SearchAndFilter';
import CalculatorMobile from '../calculator/CalculatorMobile/CalculatorMobile';


const locationCoordinates = {
  "All Areas": null,
  "Prague Castle": { lat: 50.0909, lon: 14.3983 },
  "Old Town Square": { lat: 50.0870, lon: 14.4208 },
  "Jewish Quarter": { lat: 50.0911, lon: 14.4185 },
  "Republic Square": { lat: 50.0899, lon: 14.4294 },
  "Národní Avenue": { lat: 50.0801, lon: 14.4154 },
  "Charles Bridge": { lat: 50.0865, lon: 14.4114 },
  "Lesser Town & Petřín": { lat: 50.0835, lon: 14.4037 },
  "Petřín Hill": { lat: 50.0804, lon: 14.3914 },
  "Wenceslas Square": { lat: 50.0816, lon: 14.4254 },
  "New Town": { lat: 50.0775, lon: 14.4244 },
  "Vyšehrad Castle": { lat: 50.0644, lon: 14.4203 },
  "Žižkov": { lat: 50.0864, lon: 14.4522 },
  "Holešovice": { lat: 50.1034, lon: 14.4394 },
  "Troja": { lat: 50.1169, lon: 14.4117 },
  "Smíchov": { lat: 50.0705, lon: 14.4031 },
  "Outside Prague": null
};


const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; 
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; 
};


const stripHtmlTags = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || "";
};

const AttractionsList = () => {
  const { t } = useTranslation();
  const [attractions, setAttractions] = useState(attractionStore?.allAttractions || []);
  const [filteredAttractions, setFilteredAttractions] = useState(attractionStore?.allAttractions || []);
  const [searchTerm, setSearchTerm] = useState('');
  const [location, setLocation] = useState('All Areas');
  const [sort, setSort] = useState('');
  const [isCalculatorVisible, setIsCalculatorVisible] = useState(true);
  const [viewType, setViewType] = useState('grid');
  const [likedAttractions, setLikedAttractions] = useState([]);
  const [visibleCount, setVisibleCount] = useState(15);
  const [headerTitle, setHeaderTitle] = useState("ALL ATTRACTIONS");
  const navigate = useNavigate();
  const [isCalculatorExpanded, setIsCalculatorExpanded] = useState(false);

  useEffect(() => {
    if (t && attractionStore?.allAttractions) {
      setAttractions(attractionStore?.allAttractions || []);
      setFilteredAttractions(attractionStore?.allAttractions || []);
      setLikedAttractions(attractionStore?.allAttractions?.filter(attraction => attraction.liked) || []);
    }
  }, [t, attractionStore?.allAttractions]);

  const handleShowMore = () => {
    setVisibleCount(prevCount => Math.min(prevCount + 15, filteredAttractions.length));
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    filterAndSortAttractions(term, location, sort);
  };

  const handleLocationChange = (value) => {
    setLocation(value);
    filterAndSortAttractions(searchTerm, value, sort);
  };

  const handleSortChange = (value) => {
    setSort(value);
    filterAndSortAttractions(searchTerm, location, value);
  };

  const filterAndSortAttractions = (term, location, sort, attractionsList = attractions) => {
    let filtered = attractionsList;

    filtered = filtered.filter(attraction => {
      const title = attraction?.content[languageStore.selectedLanguage]?.title || '';
      const matchesSearchTerm = term === "" || title.toLowerCase().includes(term.toLowerCase());

      let matchesLocation = true;
      if (location !== 'All Areas' && locationCoordinates[location]) {
        const { lat, lon } = locationCoordinates[location];
        const attractionLat = attraction.lat;
        const attractionLon = attraction.lon;

        if (lat && lon && attractionLat && attractionLon) {
          const distance = calculateDistance(attractionLat, attractionLon, lat, lon);
          matchesLocation = distance < 2;
        } else {
          matchesLocation = false;
        }
      }

      return matchesSearchTerm && matchesLocation;
    });

    switch (sort) {
      case t("ATTRACTIONS_order_by_az"):
        filtered.sort((a, b) => {
          const titleA = a?.content[languageStore.selectedLanguage]?.title || '';
          const titleB = b?.content[languageStore.selectedLanguage]?.title || '';
          return titleA.localeCompare(titleB);
        });
        break;
      case t("ATTRACTIONS_order_by_popularity"):
        filtered.sort((a, b) => b.mostpopular - a.mostpopular);
        break;
      case t("ATTRACTIONS_order_by_discounts"):
        filtered.sort((a, b) => parseInt(b.benefitAdult || '0') - parseInt(a.benefitAdult || '0'));
        break;
      case t("ATTRACTIONS_order_by_free"):
        filtered.sort((a, b) => {

          const priceA = parseInt(a.priceAdult) || 0;
          const priceB = parseInt(b.priceAdult) || 0;
          const benefitA = parseInt(a.benefitAdult) || 0;
          const benefitB = parseInt(b.benefitAdult) || 0;

          if (priceA === 0 && priceB !== 0) return -1;
          if (priceB === 0 && priceA !== 0) return 1;
          if (priceA !== priceB) return priceA - priceB;

          return benefitB - benefitA;
        });
        break;
      case t("ATTRACTIONS_order_by_rating"):
        filtered.sort((a, b) => b.reviewValue - a.reviewValue);
        break;
      case t("ATTRACTIONS_order_by_price"):
        filtered.sort((a, b) => parseInt(a.priceAdult || '0') - parseInt(b.priceAdult || '0'));
        break;
      default:
        break;
    }

    setFilteredAttractions(filtered);
};


  const toggleLike = (id) => {
    const updatedAttractions = attractions.map(attraction =>
      attraction._id === id ? { ...attraction, liked: !attraction.liked } : attraction
    );
    setAttractions(updatedAttractions);
    setLikedAttractions(updatedAttractions.filter(attraction => attraction.liked));
    filterAndSortAttractions(searchTerm, location, sort, updatedAttractions);
  };

  const clearAllLikes = () => {
    const updatedAttractions = attractions.map(attraction => ({
      ...attraction,
      liked: false,
    }));
    setAttractions(updatedAttractions);
    setLikedAttractions([]);
  };

  const handleRemoveFavourite = (id) => {
    const updatedAttractions = attractions.map(attraction =>
      attraction._id === id ? { ...attraction, liked: false } : attraction
    );
    setAttractions(updatedAttractions);
    setLikedAttractions(updatedAttractions.filter(attraction => attraction.liked));
    filterAndSortAttractions(searchTerm, location, sort, updatedAttractions);
  };

  const filterByCategory = (category) => {
    if (category === 'all') {
      setFilteredAttractions(attractions);
    } else if (category === 'favorites') {
      setFilteredAttractions(attractions.filter(attraction => attraction.liked));
    } else if (category === 'free') {
      setFilteredAttractions(attractions.filter(attraction => attraction.priceAdult === '0'));
    }
  };

  const handleCategoryFilter = (category, name) => {
    let filtered = [];
    setHeaderTitle(name)
    if (category === 'all') {
      filtered = attractions;
    } else {
      filtered = attractions.filter(attraction =>
        attraction.categories.includes(category)
      );
    }

    setFilteredAttractions(filtered);
  };

  return (
    <div className='attraction-list-container'>
      <SearchAndFilter setHeaderTitle={setHeaderTitle} onFilterCategory={handleCategoryFilter} />
      <div className='mobile-container'>
        <SearchHeader
          title={headerTitle}
          onSearch={handleSearch}
          onLocationChange={handleLocationChange}
          onSortChange={handleSortChange}
          onCalculatorVisibleChange={() => setIsCalculatorVisible(!isCalculatorVisible)}
          isCalculatorVisible={isCalculatorVisible}
          changeViewType={(type) => { setViewType(type); }}
          viewType={viewType}
        />
        {likedAttractions.length === 0 ? (
            <div className="placeholder">
              {t("CALCULATOR_tip")}
            </div>
          ) : null}
        <div className="attractions-wrapper">
          {viewType === 'grid' ? (
            <div className={isCalculatorVisible ? 'attractions-list-narrow' : 'attractions-list'}>
              {filteredAttractions.slice(0, visibleCount).map((attraction, index) => (
                <div key={index} className="attractions-card">
                  <AttractionCard
                    key={attraction._id}
                    id={attraction._id}
                    imageSrc={"https://static2.praguecoolpass.com/" + (attraction.images[0] || '')} 
                    title={attraction?.content[languageStore.selectedLanguage]?.title || t("No Title Available")}
                    description={stripHtmlTags(attraction?.content[languageStore.selectedLanguage]?.subtitle) || ''}
                    badgeText={attraction.benefit + " " + t("ATTRACTIONS_label_with_pass")}
                    price={`${attraction.priceAdult || 'N/A'} Kč / ${attraction.priceChild || 'N/A'} Kč`}
                    liked={attraction.liked}
                    onToggleLike={() => toggleLike(attraction._id)}
                  />
                </div>
              ))}
              {visibleCount < filteredAttractions.length && (
                <button className="show-more-button" onClick={handleShowMore}>{t("ATTRACTIONS_show_all_areas").toUpperCase()}</button>
              )}
            </div>
          ) : viewType === 'list' ? (
            <div className="attractions-list-view">
              <div className="list-header">
                <span></span>
                <span></span>
                <span>{t("ATTRACTIONS_normal_entry_price")}</span>
                <span>{t("ATTRACTIONS_coolpass_benefit")}</span>
              </div>
              {filteredAttractions.slice(0, visibleCount).map((attraction, index) => (
                <div key={index} className="list-item">
                  <img src={"https://static2.praguecoolpass.com/" + (attraction.images[0] || '')} alt={attraction?.content[languageStore?.selectedLanguage]?.title || t("No Title Available")} className="list-item-image" />
                  <span className='list-item-title'>{attraction?.content[languageStore.selectedLanguage].title || t("No Title Available")}</span>
                  <span className='list-item-price'>{`${attraction.priceAdult || 'N/A'} Kč / ${attraction.priceStudent || 'N/A'} Kč`}</span>
                  <span className='list-item-badge-text'>{attraction.benefit}</span>
                  <div onClick={() => toggleLike(attraction._id)}>
                    {attraction.liked ? <HeartFilled /> : <HeartOutlined />}
                  </div>
                </div>
              ))}
              {visibleCount < filteredAttractions.length && (
                <button className="show-more-button" onClick={handleShowMore}>{t("SHOW_MORE").toUpperCase()}</button>
              )}
            </div>
          ) : (
            <MapView
              locations={filteredAttractions}
              filterByCategory={filterByCategory}
              toggleLike={toggleLike}
            />
          )}
          {isCalculatorVisible && (
            <div className="calculator-container">
              <Calculator
                setIsCalculatorVisible={(value)=> setIsCalculatorVisible(value)}
                favourites={likedAttractions}
                onRemoveFavourite={handleRemoveFavourite}
                clearAllLikes={() => clearAllLikes()}
              />
            </div>
          )}  
          {
            likedAttractions.length > 0 ?
              <div className="calculator-mobile-container">
                <CalculatorMobile
                  onRemoveFavourite={handleRemoveFavourite}
                  clearAllLikes={() => {clearAllLikes(); setIsCalculatorExpanded(false)}} 
                  favourites={likedAttractions} 
                  isCalculatorExpanded={isCalculatorExpanded} 
                  setIsCalculatorVisible={(value)=>(setIsCalculatorExpanded(value))}
                  />
              </div>
              :
              null
          }
        </div>
      </div>
      
    </div>
  );
};

export default AttractionsList;
